import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { Time } from '../common/MomentTime';
import { JobReportSummary } from './JobReportSummary';
import PortalDataGridPro from '../common/PortalDataGridPro';
import { useSelector } from 'react-redux';
import { isUserAdmin } from '../Layout/utils';
import { WithSmallScreen } from '../common/WithSmallScreen';
import { getData } from '../../DataAccessLayer';
import { getJobReportConfig } from './config';
import { formatStudents } from '../SAM/utils';
import DataGridExportButton from '../common/DataGridExportButton';

export const JobReportCard = WithSmallScreen(
  ({ id, record, cachedData, addCachedData, data, serviceToGetDetails, ...props }) => {
    const dateFormat = 'LL';
    const smallScreenDateFormat = 'MMM D, YYYY';
    const [isExpanded, setIsExpand] = useState(props.isExpanded);
    const [columns, setColumns] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const user = useSelector((state) => state.AWSReducer.user);
    const isAdmin = isUserAdmin(user);

    const jobReportConfig = getJobReportConfig({ record, isAdmin })[id];

    useEffect(() => {
      if (isExpanded) {
        getJobDetails();
      }

      loadColumns();
    }, [isExpanded, record]);

    const loadColumns = () => {
      setColumns(jobReportConfig.columns);
    };
    const getJobDetails = async () => {
      setIsLoading(true);

      const jobId = jobReportConfig.jobId || record?.DATE + '_' + record?.CREATED_AT;

      const cachedJobData = cachedData[jobId];

      if (cachedJobData) {
        setJobs(cachedJobData);

        setIsLoading(false);
      } else {
        try {
          const data = await getData(jobReportConfig.getDetails(jobId), true);
          setJobs(formatStudents(data));
          // Don't cache the first job's result, as there's a possibility of that being an on going process, and we want to keep updating the results as and when user clicks on 'Get Report' button.
          if (record?.COMPLETED_AT) {
            addCachedData({ jobId, data });
          }
        } catch (err) {
        } finally {
          setIsLoading(false);
        }
      }
    };

    const jobId = record?.CREATED_AT.toString().slice(-5);
    const recordId = `jobReport_${record?.CREATED_AT}`;

    return (
      <Accordion
        expanded={isExpanded}
        elevation={0}
        className="accordion"
        id={recordId + '__accordion'}
      >
        <AccordionSummary
          onClick={() => {
            setIsExpand(!isExpanded);
          }}
          expandIcon={
            <Box display={'flex'} alignItems={'flex-start'} justifyContent={'flex-start'}>
              <FontAwesomeIcon
                icon={faChevronDown}
                fixedWidth
                id={recordId + '__chevron_accordionSummary'}
              />
            </Box>
          }
          id={recordId + '__accordionSummary'}
          aria-controls={recordId + '__accordionDetails'}
          sx={{
            flexDirection: 'row-reverse',
            flexGrow: 1,
            '& .MuiAccordionSummary-expandIconWrapper': {
              marginTop: '1rem',
              alignSelf: 'flex-start', // Aligns the expandIcon itself to the top
            },
          }}
          className="header"
          style={{ height: 'auto' }}
          justifyContent="center"
        >
          <Grid container direction={{ xs: 'column', sm: 'column', md: 'row' }}>
            <Grid item>
              <Typography
                id={recordId + '__title'}
                component="h3"
                className="title"
                sx={{ marginLeft: '0.5rem !important' }}
              >
                {Time.University(record?.CREATED_AT).format(
                  props.isSM ? smallScreenDateFormat : dateFormat,
                )}
              </Typography>
            </Grid>
            <Grid item xs>
              <Box
                id={recordId + '__jobReportMenu_wrapper'}
                sx={{
                  textAlign: {
                    xs: 'left',
                    sm: 'left',
                    md: 'right',
                  },
                }}
              >
                <Stack
                  direction={{
                    xs: 'row',
                    sm: 'row',
                    md: 'row',
                  }}
                  alignItems={'center'}
                  justifyContent={{
                    xs: 'flex-start',
                    md: 'flex-end',
                  }}
                >
                  <Typography
                    variant="p"
                    component={'p'}
                    whiteSpace={'nowrap'}
                    sx={{ marginLeft: '0.5rem' }}
                  >
                    {'Job - ' + jobId}
                  </Typography>
                  <span>
                    <Chip
                      label={`${jobReportConfig.successCount} ${jobReportConfig.task}`}
                      sx={{ marginLeft: 1 }}
                      size="small"
                      color="default"
                    />
                  </span>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails id={recordId + '__accordionDetails'} className="details">
          <Stack spacing={2}>
            <JobReportSummary id={id} record={record} />

            <PortalDataGridPro
              className="myOdu__resultTable"
              isLoading={isLoading}
              columns={columns}
              rows={jobs}
              getRowId={(row) => jobReportConfig.getRowId(row)}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 20 },
                },
              }}
              pagination
              slots={{
                loadingOverlay: LinearProgress,
                noRowsOverlay: () => {
                  return (
                    <Box
                      sx={{
                        margin: 1,
                      }}
                    >
                      <Typography variant="p" component="p">
                        {'No student records found.'}
                      </Typography>
                    </Box>
                  );
                },
                toolbar: () => <DataGridExportButton fileName={'SAM - ' + jobId} />,
              }}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  },
);
